/**
 * CompanyDashboardAllObjectiveDrawerMainContent
 *
 * Displays the initiatives table for the currently selected Key Result in the
 * add-all-objective flow. Provides a gated "Add Initiative" action depending on
 * selection and permissions, and formats data for the shared MUITable.
 */
import React, { useEffect, useMemo, useState } from "react";
import addREC from "../../../../../assets/icons/addREC.svg";
import Status from "../../../../request-dashboard/request/status/status";
import { MUITable } from "../../../../../common/components";
import CompanyDashboardInitiativeAddDrawer from "../../../company-dashboard-initiative/company-dashboard-initiative-add-drawer/company-dashboard-initiative-add-drawer";
import CompanyDashboardAddInitiativePopup from "../../company-dashboard-add-popup/company-dashboard-add-initiative-popup/company-dashboard-add-initiative-popup";
import "./company-dashboard-all-objective-drawer-main-content.scss";
import { Drawer, Tooltip } from "@mui/material";
import dayjs from "dayjs";
import { getDepartmentOwners } from "../../../../../api/initiative";
import { usePermissions } from "../../../../../common/hooks/usePermissions";
import MODULES from "../../../../../constants/modules";

const CompanyDashboardAllObjectiveDrawerMainContent: any = (props: any) => {// NOSONAR
  const {
    listOfKeyResultsData,
    setListOfKeyResultsData,
    saveInitiative,
    employeesList,
    statusListInitiatives,
    statusListKeyResults,
    statusListObjectives,
    setLoading,
    objectId,
    setAlertMessage,
    setAlertType,
    setAlertOpen,
    fetchObjectivesById,
  } = props;
  const [isOpenAddPopup, setIsOpenAddPopup] = useState(false);
  const [isShowInitiativeTableDrawer, setIsShowInitiativeTableDrawer] =
    useState(false);
  const [selectedInitiativeData, setSelectedInitiativeData] =
    useState<any>(null);
  const [keyResultId, setKeyResultId] = useState<number | null>(null);
  const [selectedInitiativesData, setSelectedInitiativesData] = useState([]);
  const [departmentsList, setDepartmentsList] = useState<any[]>([]);
  const { canWrite: canWriteInitiatives } = usePermissions(MODULES.INITIATIVES);

  useEffect(() => {
    const selectedKey = listOfKeyResultsData?.find(
      (data: any) => data?.isSelected,
    );
    if (selectedKey) {
      setSelectedInitiativesData(selectedKey?.initiatives);
    } else {
      setSelectedInitiativesData([]);
    }
  }, [listOfKeyResultsData]);

  const fetchDepartmentOwners = async () => {
    try {
      const response = await getDepartmentOwners();
      setDepartmentsList(response?.data?.data);
    } catch (error: any) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchDepartmentOwners();
  }, []);

  const handleOpenAddPopup = () => {
    setIsOpenAddPopup(true);
  };

  const handleCloseAddPopup = () => {
    setIsOpenAddPopup(false);
  };
  const hasAnyKeyResults =
    Array.isArray(listOfKeyResultsData) && listOfKeyResultsData.length > 0;
  const selectedKey = listOfKeyResultsData?.find((d: any) => d?.isSelected);
  const canAddInitiative = !!selectedKey;
  let addInitiativeTooltip = "Add Initiative";
  if (!hasAnyKeyResults) {
    addInitiativeTooltip =
      "Please create a Key Result before adding an Initiative";
  } else if (!selectedKey) {
    addInitiativeTooltip = "Select a Key Result to add Initiatives";
  }
  const headers = [
    { key: "title", label: "Title" },
    { key: "startDate", label: "Start Date" },
    { key: "endDate", label: "End Date" },
    { key: "percentageComplete", label: "Percentage Complete" },
    { key: "status", label: "Status" },
  ];
  useEffect(() => {
    if (!isShowInitiativeTableDrawer) {
      fetchObjectivesById();
    }
  }, [isShowInitiativeTableDrawer, objectId]);

  const onRowClick = (rowData: any) => {
    setIsShowInitiativeTableDrawer(true);
    setSelectedInitiativeData(rowData);
  };

  const formattedData = useMemo(() => {
    if (
      Array.isArray(selectedInitiativesData) &&
      selectedInitiativesData.length > 0
    ) {
      return selectedInitiativesData.map((item: any) => {
        const statusText =
          typeof item?.status === "string"
            ? item.status
            : item?.status?.statusName || "-";
        const isApprovalPending = item?.isApproved === false;
        const isFaded = isApprovalPending;
        return {
          id: item?.id,
          title: item?.title || "-",
          startDate: item?.startDate
            ? dayjs(item.startDate).format("MM-DD-YYYY")
            : "-",
          endDate: item?.endDate
            ? dayjs(item.endDate).format("MM-DD-YYYY")
            : "-",
          percentageComplete: Math.round(item?.percentageComplete || 0) + "%",
          status: <Status status={statusText} />,
          owners: item?.owners,
          isInitiativePage: true,
          data: item,
          isFaded,
          isApprovalPending,
        };
      });
    }
    return [];
  }, [selectedInitiativesData]);

  return (
    <>
      {selectedInitiativesData?.length === 0 ? (
        <div className="company-dashboard-all-objective-add-drawer-main-content">
          <div className="company-dashboard-all-objective-add-drawer-main-content-section">
            <div className="company-dashboard-all-objective-add-drawer-main-content-section-label">
              <div>List Of Initiatives</div>
              {canWriteInitiatives && (
                <div>
                  {canAddInitiative ? (
                    <button
                      onClick={handleOpenAddPopup}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                      }}
                    >
                      <img
                        src={addREC}
                        alt="Add"
                        style={{ cursor: "pointer" }}
                      />
                    </button>
                  ) : (
                    <Tooltip title={addInitiativeTooltip}>
                      <img
                        src={addREC}
                        alt="Add"
                        style={{ opacity: 0.4, cursor: "not-allowed" }}
                      />
                    </Tooltip>
                  )}
                </div>
              )}
            </div>

            <div className="company-dashboard-all-objective-add-drawer-main-content-add-section-content">
              <div className="company-dashboard-all-objective-add-drawer-main-content-add-section-content-label">
                You don't have any key results
              </div>
              {canWriteInitiatives && (
                <div className="company-dashboard-all-objective-add-drawer-main-content-add-section-content-button">
                  {canAddInitiative ? (
                    <button onClick={handleOpenAddPopup}>
                      Add Initiatives
                    </button>
                  ) : (
                    <Tooltip title={addInitiativeTooltip}>
                      <span>
                        <button
                          style={{ opacity: 0.6, cursor: "not-allowed" }}
                          disabled
                        >
                          Add Initiatives
                        </button>
                      </span>
                    </Tooltip>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* non-empty block - same gating on the add icon in header */
        <div className="company-dashboard-all-objective-add-drawer-main-content">
          <div className="company-dashboard-all-objective-add-drawer-main-content-section">
            <div className="company-dashboard-all-objective-add-drawer-main-content-section-label">
              <div>List Of Initiatives</div>
              <div>
                {canAddInitiative ? (
                  <button
                    onClick={handleOpenAddPopup}
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                    }}
                  >
                    <img
                      src={addREC}
                      alt="Close"
                      style={{ cursor: "pointer" }}
                    />
                  </button>
                ) : (
                  <Tooltip title={addInitiativeTooltip}>
                    <img
                      src={addREC}
                      alt="Close"
                      style={{ opacity: 0.4, cursor: "not-allowed" }}
                    />
                  </Tooltip>
                )}
              </div>
            </div>

            <div className="company-dashboard-all-objective-add-drawer-main-content-section-content">
              <MUITable
                columnNames={headers}
                rowsData={formattedData}
                onRowClick={onRowClick}
                renderExpandedRow={(row: any) => {
                  if (row.isApprovalPending) {
                    return (
                      <div
                        style={{
                          marginTop: "5px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        under review
                      </div>
                    );
                  }
                  return null;
                }}
              />
            </div>
          </div>
        </div>
      )}

      <CompanyDashboardAddInitiativePopup
        isOpenAddPopup={isOpenAddPopup}
        onCancel={handleCloseAddPopup}
        listOfInitiativesData={listOfKeyResultsData}
        setListOfInitiativesData={setListOfKeyResultsData}
        selectedInitiativesData={selectedInitiativesData}
        setSelectedInitiativesData={setSelectedInitiativesData}
        onSave={saveInitiative}
        employeesList={employeesList}
        statusListInitiatives={statusListInitiatives}
        departmentsList={departmentsList}
      />
      <Drawer
        anchor={"bottom"}
        open={isShowInitiativeTableDrawer}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return;
          }
          setIsShowInitiativeTableDrawer(false);
          setSelectedInitiativeData(null);
        }}
      >
        <CompanyDashboardInitiativeAddDrawer
          setIsShowInitiativeDrawer={setIsShowInitiativeTableDrawer}
          employeesList={employeesList}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          setAlertOpen={setAlertOpen}
          setLoading={setLoading}
          initiativeId={selectedInitiativeData?.data?.pk}
          setInitiativeId={() => {}}
          objectiveId={objectId}
          setObjectiveId={() => {}}
          keyResultId={keyResultId}
          setKeyResultId={setKeyResultId}
          fetchInitiatives={() => {}}
          statusListKeyResults={statusListKeyResults}
          statusListObjectives={statusListObjectives}
          statusListInitiatives={statusListInitiatives}
          departmentsList={departmentsList}
        />
      </Drawer>
    </>
  );
};

export default CompanyDashboardAllObjectiveDrawerMainContent;
